import { apiFetch } from '@crowdex/host-kit/renderer/http';
import { type PullRequestSummary, pullRequestNumberFromUrl } from '@crowdex/workspace-protocol';
import { useEffect, useSyncExternalStore } from 'react';
import { onStreamedEvent } from '@/features/notifications/notification-feed';
import { isLocalMachine } from '@/lib/local-runtime';
import {
  boundPullRequest,
  type ConversationPullRequestBinding,
  type ConversationPullRequestsView,
} from '@/lib/pull-request';

/**
 * A conversation's pull requests as this machine's server binds them (several at once: opened,
 * printed by a tool call, of a branch it pushed, linked), read once for every view showing them
 * (the header, the Summary): again every 30 s while one is shown, when the window comes back,
 * and when the server says they changed (after a turn that pushed). One read at a time.
 */
export const CONVERSATION_PULL_REQUESTS_POLL_MS = 30_000;
/** Views mounting together share the read made this recently. */
const REUSE_MS = 2_000;
const CHANGED_EVENT = 'crowdex/conversation.changed';
const JSON_HEADERS = { 'content-type': 'application/json' } as const;

type Entry = {
  view: ConversationPullRequestsView | null;
  reading: Promise<void> | null;
  at: number;
  listeners: Set<() => void>;
};

const entries = new Map<string, Entry>();

function entryOf(conversationId: string): Entry {
  let entry = entries.get(conversationId);
  if (entry === undefined) {
    entry = { view: null, reading: null, at: 0, listeners: new Set() };
    entries.set(conversationId, entry);
  }
  return entry;
}

const path = (conversationId: string) =>
  `/api/local/conversations/${encodeURIComponent(conversationId)}/pull-requests`;

/** Reads a conversation's pull requests again, unless a read of them is in flight. */
export function refreshConversationPullRequests(
  conversationId: string,
  options: { fresh?: boolean } = {},
): Promise<void> {
  const entry = entryOf(conversationId);
  if (entry.reading !== null) return entry.reading;
  entry.reading = apiFetch<ConversationPullRequestsView>(
    `${path(conversationId)}${options.fresh ? '?fresh=1' : ''}`,
    { credentials: 'include', cache: 'no-store' },
  )
    .then((view) => {
      entry.view = view;
    })
    // Unreachable for now: the last ones read stay shown.
    .catch(() => undefined)
    .finally(() => {
      entry.at = Date.now();
      entry.reading = null;
      for (const listener of entry.listeners) listener();
    });
  return entry.reading;
}

/**
 * A conversation's bindings after reading its pull requests again (the sidebar's record of them);
 * null when they could not be read.
 */
export async function readConversationPullRequestBindings(
  conversationId: string,
): Promise<ConversationPullRequestBinding[] | null> {
  await refreshConversationPullRequests(conversationId);
  const view = entries.get(conversationId)?.view ?? null;
  if (view === null) return null;
  return [
    ...view.pullRequests.map((entry) => ({
      url: entry.url,
      branch: entry.branch,
      dismissed: false,
    })),
    ...view.dismissed.map((url) => ({ url, branch: null, dismissed: true })),
  ];
}

/** Links a pull request of the folder's repository to the conversation (pasted or attached). */
export async function linkConversationPullRequest(
  conversationId: string,
  url: string,
): Promise<void> {
  await apiFetch(path(conversationId), {
    method: 'POST',
    credentials: 'include',
    headers: JSON_HEADERS,
    body: JSON.stringify({ url }),
  });
  await refreshConversationPullRequests(conversationId);
}

/** Removes a pull request from the conversation, or declines the one offered. */
export async function removeConversationPullRequest(
  conversationId: string,
  url: string,
): Promise<void> {
  await apiFetch(path(conversationId), {
    method: 'DELETE',
    credentials: 'include',
    headers: JSON_HEADERS,
    body: JSON.stringify({ url }),
  });
  await refreshConversationPullRequests(conversationId);
}

const NONE = () => () => undefined;

/**
 * The conversation's pull requests, kept current while `active` (shown, on this machine); null
 * before the first read, or for none.
 */
export function useConversationPullRequests(
  conversationId: string | null,
  active: boolean,
): ConversationPullRequestsView | null {
  const enabled = active && conversationId !== null && conversationId !== '' && isLocalMachine();
  const id = enabled ? conversationId : null;
  const view = useSyncExternalStore(
    id === null
      ? NONE
      : (listener) => {
          const entry = entryOf(id);
          entry.listeners.add(listener);
          return () => entry.listeners.delete(listener);
        },
    () => (id === null ? null : (entries.get(id)?.view ?? null)),
  );
  useEffect(() => {
    if (id === null) return;
    const stale = (age: number) => Date.now() - entryOf(id).at >= age;
    if (stale(REUSE_MS)) void refreshConversationPullRequests(id);
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible' && stale(CONVERSATION_PULL_REQUESTS_POLL_MS / 2))
        void refreshConversationPullRequests(id);
    }, CONVERSATION_PULL_REQUESTS_POLL_MS);
    const onFocus = () => {
      if (stale(REUSE_MS)) void refreshConversationPullRequests(id);
    };
    window.addEventListener('focus', onFocus);
    const stop = onStreamedEvent((event) => {
      if (
        event.type === CHANGED_EVENT &&
        event.threadId === id &&
        event.fields['change'] === 'pull-request'
      )
        void refreshConversationPullRequests(id);
    });
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      stop();
    };
  }, [id]);
  return view;
}

const sameUrl = (left: string, right: string) =>
  left.replace(/\/+$/, '').toLowerCase() === right.replace(/\/+$/, '').toLowerCase();

/** Whether the user removed this pull request from the conversation. */
export function isDismissedPullRequest(
  view: ConversationPullRequestsView | null,
  url: string | null | undefined,
): boolean {
  if (view === null || url === null || url === undefined) return false;
  return view.dismissed.some((dismissed) => sameUrl(dismissed, url));
}

/**
 * The pull requests a conversation's views show, first its branch's one (`primary`, unless the
 * user removed it), then those bound to it, each once.
 */
export function conversationPullRequestList(
  primary: PullRequestSummary | null,
  view: ConversationPullRequestsView | null,
): PullRequestSummary[] {
  const shown: PullRequestSummary[] = [];
  const add = (pullRequest: PullRequestSummary) => {
    if (!shown.some((entry) => sameUrl(entry.url, pullRequest.url))) shown.push(pullRequest);
  };
  if (primary !== null && !isDismissedPullRequest(view, primary.url)) add(primary);
  for (const binding of view?.pullRequests ?? []) add(boundPullRequest(binding));
  return shown;
}

/** Whether a URL is a pull request one may link (its server checks it is of the folder's repository). */
export function isPullRequestUrl(url: string): boolean {
  return pullRequestNumberFromUrl(url.trim()) !== null;
}
